import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AudioRecorder.module.scss";
import logger from "../../../../utils/logger";
import { AUDIO_INPUT_TYPE } from "../../../../constants/process";

interface AudioRecorderProps {
  onAudioRecorded: (
    audioBlob: Blob,
    waveformData?: number[],
    duration?: number,
  ) => void;
  onCancel: () => void;
  isDarkTheme?: boolean;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onAudioRecorded,
  onCancel,
  isDarkTheme = true,
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingPartial, setIsPlayingPartial] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [playbackTime, setPlaybackTime] = useState(0);
  const [hasAudio, setHasAudio] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [partialAudioUrl, setPartialAudioUrl] = useState<string>("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const partialAudioRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Arrays para waveform com efeito "correndo"
  const waveformHistoryRef = useRef<number[]>(Array(80).fill(0)); // Histórico de amplitude
  const frameCountRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);
  const waveformDataRef = useRef<number[]>([]);
  const maxRecentRef = useRef<number>(1); // Para normalização automática

  // Sistema de threshold dinâmico para filtrar ruído de fundo
  const ambientNoiseRef = useRef<number>(0); // Ruído ambiente médio
  const noiseCounterRef = useRef<number>(0); // Contador para cálculo da média
  const calibrationTimeRef = useRef<number>(0); // Tempo de calibração (2 segundos)
  const isCalibrationCompleteRef = useRef<boolean>(false);

  // Iniciar gravação automaticamente
  useEffect(() => {
    startRecording();
    return () => {
      cleanup();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincronizar ref com estado
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    if (partialAudioUrl) {
      URL.revokeObjectURL(partialAudioUrl);
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (partialAudioRef.current) {
      partialAudioRef.current.pause();
      partialAudioRef.current = null;
    }
  }, [audioUrl, partialAudioUrl]);

  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  // Função para gerar dados de waveform para visualização posterior
  const generateWaveformData = useCallback(() => {
    if (waveformHistoryRef.current.length === 0) return [];

    // Converter dados de altura das barras em dados de waveform mais compactos (60 barras para visualização)
    const targetSamples = 60;
    const sourceLength = waveformHistoryRef.current.length;
    const ratio = sourceLength / targetSamples;

    const waveformData: number[] = [];

    for (let i = 0; i < targetSamples; i++) {
      let sum = 0;
      let count = 0;

      // Calcular média dos dados de altura correspondentes
      const startIndex = Math.floor(i * ratio);
      const endIndex = Math.min(Math.floor((i + 1) * ratio), sourceLength);

      for (let j = startIndex; j < endIndex; j++) {
        sum += waveformHistoryRef.current[j];
        count++;
      }

      const average = count > 0 ? sum / count : 1;

      // Normalizar para faixa apropriada para visualização (2-16)
      let normalizedHeight;
      if (average <= 1) {
        normalizedHeight = 2; // Silêncio
      } else if (average <= 10) {
        normalizedHeight = 4 + (average - 1) * 0.5; // Baixa amplitude
      } else if (average <= 20) {
        normalizedHeight = 6 + (average - 10) * 0.4; // Média amplitude
      } else {
        normalizedHeight = Math.min(16, 8 + (average - 20) * 0.2); // Alta amplitude
      }

      waveformData.push(Math.round(normalizedHeight));
    }

    return waveformData;
  }, []);

  // Função para desenhar waveform com efeito "correndo" como WhatsApp
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;

    // Verificar estado atual
    const currentIsRecording = mediaRecorderRef.current?.state === "recording";

    if (!canvas || !analyser) {
      // Continuar tentando se deveria estar gravando
      if (currentIsRecording || isRecording) {
        animationRef.current = requestAnimationFrame(drawWaveform);
      }
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    // Capturar dados de amplitude em tempo real (conforme instruções)
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(dataArray);

    // Calcular amplitude média do frame atual (conforme exemplo)
    const rawAmplitude =
      dataArray.reduce((acc, val) => acc + Math.abs(val - 128), 0) /
      dataArray.length;

    // 1. Aplicar GAIN (fator de ampliação) conforme instruções
    const GAIN = 4; // Experimente valores de 2 a 6 até ficar confortável
    const amplified = Math.min(rawAmplitude * GAIN, 128); // Limita para não estourar

    // 2. Sistema de calibração de ruído ambiente (primeiros 2 segundos)
    calibrationTimeRef.current += 1 / 60; // Aproximadamente 60fps

    if (calibrationTimeRef.current < 2 && !isCalibrationCompleteRef.current) {
      // Período de calibração - medir ruído ambiente
      ambientNoiseRef.current += amplified;
      noiseCounterRef.current++;

      if (calibrationTimeRef.current >= 2) {
        // Finalizar calibração
        const averageNoise = ambientNoiseRef.current / noiseCounterRef.current;
        ambientNoiseRef.current = averageNoise;
        isCalibrationCompleteRef.current = true;
      }
    }

    // 3. Aplicar threshold dinâmico baseado no ruído ambiente
    let filteredAmplitude = amplified;

    if (isCalibrationCompleteRef.current) {
      // Usar threshold dinâmico baseado no ruído ambiente + margem de segurança
      const DYNAMIC_THRESHOLD = ambientNoiseRef.current * 1.5; // 50% acima do ruído ambiente
      const MIN_THRESHOLD = 6; // Threshold mínimo absoluto
      const FINAL_THRESHOLD = Math.max(DYNAMIC_THRESHOLD, MIN_THRESHOLD);

      // Aplicar filtro: zerar amplitudes abaixo do threshold
      if (amplified < FINAL_THRESHOLD) {
        filteredAmplitude = 0;
      } else {
        // Subtrair o threshold para suavizar a transição
        filteredAmplitude = amplified - FINAL_THRESHOLD * 0.3;
      }
    } else {
      // Durante calibração, usar threshold fixo conservador
      const CALIBRATION_THRESHOLD = 8;
      if (amplified < CALIBRATION_THRESHOLD) {
        filteredAmplitude = 0;
      }
    }

    // 4. Normalização automática baseada no maior valor recente (efeito profissional)
    const RECENT_FACTOR = 0.96; // Quanto menor, mais rápido se ajusta
    maxRecentRef.current = Math.max(
      filteredAmplitude,
      maxRecentRef.current * RECENT_FACTOR,
    );

    // 5. Amplitude final normalizada
    const normalizedAmplitude =
      maxRecentRef.current > 1
        ? (filteredAmplitude / maxRecentRef.current) * 128
        : filteredAmplitude;

    // Atualizar histórico - efeito "correndo" (push/shift)
    waveformHistoryRef.current.push(normalizedAmplitude);
    waveformHistoryRef.current.shift();

    // Configurar canvas
    const width = canvas.width;
    const height = canvas.height;

    // Limpar canvas
    ctx.clearRect(0, 0, width, height);

    // Configurações das barras
    const barCount = waveformHistoryRef.current.length;
    const barWidth = width / barCount;

    let hasAnySound = false;
    let maxAmplitude = 0;

    // Desenhar barras do histórico (efeito onda correndo)
    waveformHistoryRef.current.forEach((amp, i) => {
      maxAmplitude = Math.max(maxAmplitude, amp);

      // Detectar se há som significativo (após filtro de threshold)
      if (amp > 1) {
        // Valor bem baixo já que foi filtrado
        hasAnySound = true;
      }

      // Calcular altura da barra usando valor filtrado e normalizado
      let barHeight;
      if (amp <= 1) {
        barHeight = 2; // Silêncio - linha bem baixa (pontos)
      } else {
        // Usar amplitude filtrada para altura do canvas
        const normalizedAmp = amp / 128;
        barHeight = Math.max(2, normalizedAmp * height * 0.9);
      }

      // Posição da barra
      const x = i * barWidth;
      const y = (height - barHeight) / 2;

      // Cor das barras (mesmo estilo anterior)
      ctx.fillStyle = isDarkTheme
        ? "rgba(139, 69, 255, 0.8)"
        : "rgba(139, 69, 255, 0.9)";

      // Desenhar barra
      ctx.fillRect(x, y, barWidth * 0.8, barHeight);
    });

    setHasAudio(hasAnySound);

    // Incrementar contador de frames
    frameCountRef.current++;

    // Atualizar dados de waveform a cada 10 frames para não sobrecarregar
    if (frameCountRef.current % 10 === 0) {
      const currentWaveformData = generateWaveformData();
      waveformDataRef.current = currentWaveformData;
    }

    // Efeito de brilho quando há som significativo (valores já filtrados)
    if (hasAnySound && maxAmplitude > 5) {
      ctx.shadowBlur = 3;
      ctx.shadowColor = "#8B45FF";
      ctx.fillStyle = "rgba(139, 69, 255, 1)";

      // Redesenhar com brilho apenas barras com movimento real
      waveformHistoryRef.current.forEach((amp, i) => {
        if (amp > 3) {
          // Threshold para brilho (já filtrado)
          let barHeight;
          if (amp <= 1) {
            barHeight = 2;
          } else {
            const normalizedAmp = amp / 128;
            barHeight = Math.max(2, normalizedAmp * height * 0.9);
          }

          const x = i * barWidth;
          const y = (height - barHeight) / 2;

          ctx.fillRect(x, y, barWidth * 0.8, barHeight);
        }
      });

      ctx.shadowBlur = 0; // Resetar sombra
    }

    // Continuar animação se MediaRecorder estiver ativo
    if (currentIsRecording || isRecording) {
      animationRef.current = requestAnimationFrame(drawWaveform);
    }
  }, [isDarkTheme, isRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRecording = useCallback(async () => {
    try {
      // Resetar calibração de ruído ambiente para nova gravação
      ambientNoiseRef.current = 0;
      noiseCounterRef.current = 0;
      calibrationTimeRef.current = 0;
      isCalibrationCompleteRef.current = false;

      // Configurações otimizadas para captura de voz sem ruído de fundo
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1, // Mono para melhor detecção de voz
        },
      });

      // Configurar análise de áudio em tempo real
      const audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();

      // Resumir contexto se suspenso
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Configuração otimizada para efeito de waveform "correndo" (conforme instruções)
      analyser.fftSize = 64; // Baixo para menos barras, maior = mais detalhes
      analyser.smoothingTimeConstant = 0.2; // Suavização moderada
      analyser.minDecibels = -90; // Captar até sons bem baixos
      analyser.maxDecibels = -10; // Faixa dinâmica adequada

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const currentIsPaused = isPausedRef.current;

        if (currentIsPaused) {
          // Se foi pausado, criar áudio parcial para reprodução
          if (audioChunksRef.current.length > 0) {
            const partialBlob = new Blob(audioChunksRef.current, {
              type: AUDIO_INPUT_TYPE,
            });
            const partialUrl = URL.createObjectURL(partialBlob);
            setPartialAudioUrl(partialUrl);
          }
        } else {
          // Se não foi pausado, criar áudio final
          const blob = new Blob(audioChunksRef.current, {
            type: AUDIO_INPUT_TYPE,
          });
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
          setIsFinished(true);

          // Parar todas as tracks do stream
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      startTimer();

      // Iniciar waveform após um pequeno delay
      setTimeout(() => {
        drawWaveform();
      }, 100);
    } catch (error) {
      logger.error("Erro ao iniciar gravação:", error);
      onCancel();
    }
  }, [onCancel, drawWaveform]);

  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      // Atualizar ref ANTES de parar a gravação
      setIsPaused(true);
      isPausedRef.current = true;

      // Parar a gravação temporariamente para poder acessar os chunks
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Parar o waveform durante a pausa
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    }
  };

  const resumeRecording = () => {
    if (isPaused) {
      // Parar reprodução parcial se estiver tocando
      if (isPlayingPartial && partialAudioRef.current) {
        partialAudioRef.current.pause();
        setIsPlayingPartial(false);
      }

      // Atualizar estados e ref
      setIsPaused(false);
      isPausedRef.current = false;

      // Reiniciar a gravação com um novo MediaRecorder
      restartRecording();
    }
  };

  const restartRecording = useCallback(async () => {
    try {
      // Resetar calibração de ruído ambiente para gravação retomada
      ambientNoiseRef.current = 0;
      noiseCounterRef.current = 0;
      calibrationTimeRef.current = 0;
      isCalibrationCompleteRef.current = false;

      // Reutilizar o stream atual se ainda estiver ativo, senão criar um novo
      let stream =
        audioContextRef.current?.state === "running"
          ? mediaRecorderRef.current?.stream
          : null;

      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 1,
          },
        });

        // Reconectar audio context se necessário
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== "closed"
        ) {
          const source =
            audioContextRef.current.createMediaStreamSource(stream);
          source.connect(analyserRef.current!);
        }
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const currentIsPaused = isPausedRef.current;
        const blob = new Blob(audioChunksRef.current, {
          type: AUDIO_INPUT_TYPE,
        });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);

        if (!currentIsPaused) {
          // Se não está pausado, significa que parou definitivamente
          setAudioUrl(url);
          setIsFinished(true);

          // Parar todas as tracks do stream
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        setPartialAudioUrl(url);
        setIsFinished(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setIsPlaying(false);
      setIsPlayingPartial(false);

      // Limpar reprodução parcial se existir
      if (partialAudioRef.current) {
        partialAudioRef.current.pause();
        partialAudioRef.current = null;
      }
      if (partialAudioUrl) {
        URL.revokeObjectURL(partialAudioUrl);
        setPartialAudioUrl("");
      }

      startTimer();

      // Reiniciar waveform
      setTimeout(() => {
        drawWaveform();
      }, 100);
    } catch (error) {
      logger.error("Erro ao retomar gravação:", error);
      onCancel();
    }
  }, [partialAudioUrl, onCancel, drawWaveform]);

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      // Gerar dados finais de waveform
      const finalWaveformData = generateWaveformData();
      waveformDataRef.current = finalWaveformData;
    }
  };

  const handleTrashClick = () => {
    // Parar gravação se estiver ativa
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    // Parar qualquer reprodução de áudio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (partialAudioRef.current) {
      partialAudioRef.current.pause();
      partialAudioRef.current = null;
    }

    // Resetar estados
    setIsRecording(false);
    setIsPaused(false);
    setIsPlaying(false);
    setIsPlayingPartial(false);
    setIsFinished(false);
    setHasAudio(false);
    setRecordingTime(0);
    setPlaybackTime(0);

    // Limpar tudo e cancelar
    cleanup();
    onCancel();
  };

  const handleSendClick = () => {
    if (audioBlob) {
      onAudioRecorded(audioBlob, waveformDataRef.current, recordingTime);
    } else {
      // Se ainda está gravando, parar e enviar
      stopRecording();
      // Dar um tempo para o blob ser criado
      setTimeout(() => {
        if (audioChunksRef.current.length > 0) {
          const blob = new Blob(audioChunksRef.current, {
            type: AUDIO_INPUT_TYPE,
          });
          onAudioRecorded(blob, waveformDataRef.current, recordingTime);
        }
      }, 100);
    }
    cleanup();
  };

  const handlePlayPause = () => {
    if (isFinished && audioBlob) {
      // Modo reprodução do áudio final
      if (!audioRef.current) {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.ontimeupdate = () => {
          setPlaybackTime(audio.currentTime);
        };

        audio.onended = () => {
          setIsPlaying(false);
          setPlaybackTime(0);
          audioRef.current = null;
        };
      }

      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      // Modo gravação - pausar/retomar
      if (isPaused) {
        resumeRecording();
      } else {
        pauseRecording();
      }
    }
  };

  // Nova função para reproduzir áudio parcial durante a pausa
  const handlePartialPlayPause = () => {
    if (isPaused) {
      if (!partialAudioUrl) {
        return;
      }

      if (!partialAudioRef.current) {
        const audio = new Audio(partialAudioUrl);
        partialAudioRef.current = audio;

        // Atualizar tempo durante reprodução parcial
        setPlaybackTime(0);
        let timeCounter = 0;
        const interval = setInterval(() => {
          timeCounter++;
          setPlaybackTime(timeCounter);
        }, 1000);

        audio.onended = () => {
          setIsPlayingPartial(false);
          setIsPlaying(false);
          setPlaybackTime(recordingTime); // Voltar ao tempo total gravado
          clearInterval(interval);
          partialAudioRef.current = null;
        };

        audio.onerror = (e) => {
          logger.error("❌ Erro ao reproduzir áudio parcial:", e);
          setIsPlayingPartial(false);
          clearInterval(interval);
          partialAudioRef.current = null;
        };
      }

      if (isPlayingPartial) {
        partialAudioRef.current?.pause();
        setIsPlayingPartial(false);
        setPlaybackTime(recordingTime); // Voltar ao tempo total gravado
      } else {
        partialAudioRef.current
          ?.play()
          .then(() => {
            setIsPlayingPartial(true);
          })
          .catch((error) => {
            logger.error("❌ Erro ao reproduzir áudio parcial:", error);
            setIsPlayingPartial(false);
            partialAudioRef.current = null;
          });
      }
    }
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return "0:00";
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const themeClass = isDarkTheme ? styles.dark : styles.light;

  return (
    <div className={`${styles.audioRecorder} ${themeClass}`}>
      {/* Botão Lixeira (posição do botão de anexo) */}
      <button
        className={styles.trashButton}
        onClick={handleTrashClick}
        title={t("chatjess.audio.discardRecording") as string}
      >
        <i className="fas fa-trash" />
      </button>

      {/* Indicador de Gravação / Botão de Reprodução quando pausado */}
      <div className={styles.recordingIndicator}>
        {isPaused ? (
          <button
            className={`${styles.playIndicatorButton} ${isPlayingPartial ? styles.playing : ""}`}
            onClick={handlePartialPlayPause}
            title={
              isPlayingPartial
                ? "Pausar reprodução"
                : "Reproduzir gravação parcial"
            }
          >
            <i className={`fas ${isPlayingPartial ? "fa-pause" : "fa-play"}`} />
          </button>
        ) : (
          <div
            className={`${styles.recordingDot} ${isPaused ? styles.paused : ""} ${isFinished ? styles.finished : ""}`}
          />
        )}
        <span className={styles.timeDisplay}>
          {formatTime(isPlayingPartial ? playbackTime : recordingTime)}
        </span>
      </div>

      {/* Waveform Progress Bar (ocupando quase toda a largura) */}
      <div className={styles.waveformProgressBar}>
        <div className={styles.waveformContainer}>
          {isRecording || isPaused || isFinished ? (
            <div
              className={`${styles.waveform} ${hasAudio ? styles.withAudio : ""}`}
            >
              <canvas
                ref={canvasRef}
                width={600}
                height={50}
                className={styles.waveformCanvas}
              />
            </div>
          ) : (
            <div className={styles.flatLine} />
          )}
        </div>
      </div>

      {/* Botão Pause/Play */}
      <button
        className={styles.pausePlayButton}
        onClick={handlePlayPause}
        title={
          isFinished
            ? isPlaying
              ? (t("chatjess.audio.pausePlayback") as string)
              : (t("chatjess.audio.playAudio") as string)
            : isPaused
              ? (t("chatjess.audio.resumeRecording") as string)
              : (t("chatjess.audio.pauseRecording") as string)
        }
      >
        <i
          className={`fas ${
            isFinished
              ? isPlaying
                ? "fa-pause"
                : "fa-play"
              : isPaused
                ? "fa-microphone"
                : "fa-pause"
          }`}
        />
      </button>

      {/* Botão Enviar (posição do botão de envio) */}
      <button
        className={styles.sendButton}
        onClick={handleSendClick}
        title={t("chatjess.audio.sendAudio") as string}
      >
        <i className="fas fa-paper-plane" />
      </button>
    </div>
  );
};

export default AudioRecorder;
